import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Page } from '../../layout/AppLayout.jsx'
import Card from '../../components/ui/Card.jsx'
import Badge from '../../components/ui/Badge.jsx'
import Button, { IconButton } from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'
import { formatCurrency, formatDate } from '../../lib/format.js'
import { invoiceStatus, peppolBadge } from '../invoices/invoiceStatus.js'
import { isValidVat, suggestBelgianVat } from '../../lib/validation.js'
import PartyFormModal from './PartyFormModal.jsx'
import PeppolPanel from './PeppolPanel.jsx'

/*
 * Customer / supplier detail — inventory §3.3 & §4.3. Info card + Peppol panel,
 * plus delivery addresses (customers) or read-only bank accounts (suppliers),
 * and the party's related invoices. Edit reuses the internal form modal.
 */
export default function PartyDetailPage({ config }) {
  const { id } = useParams()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { confirm, toast } = useFeedback()

  const [party, setParty] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [addresses, setAddresses] = useState([])
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [editing, setEditing] = useState(false)
  const [addingAddr, setAddingAddr] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api.get(`${config.endpoint}/${id}`)
      .then(({ data }) => setParty(data?.customer || data?.supplier || data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
    api.get('/invoices/', { params: { [config.invoiceParam]: id, per_page: 50 } })
      .then(({ data }) => setInvoices(data?.invoices || [])).catch(() => setInvoices([]))
    if (config.hasDeliveryAddresses) {
      api.get(`${config.endpoint}/${id}/delivery-addresses`)
        .then(({ data }) => setAddresses(data?.delivery_addresses || [])).catch(() => setAddresses([]))
    }
  }, [config, id])

  useEffect(() => { load() }, [load])

  const deleteAddress = async (addr) => {
    const ok = await confirm({ tone: 'danger', destructive: true, title: t('parties.deliveryAddresses.confirmDelete'), message: addr.address, confirmLabel: t('common.delete'), cancelLabel: t('common.cancel') })
    if (!ok) return
    try { await api.delete(`${config.endpoint}/${id}/delivery-addresses/${addr.id}`); toast(t('parties.toast.deleted'), 'success'); load() }
    catch { toast(t('parties.toast.error'), 'error') }
  }

  if (loading) return <Page title={t(`parties.${config.key}.detailTitle`)}><Card>{t('common.loading')}</Card></Page>
  if (notFound || !party) {
    return (
      <Page title={t(`parties.${config.key}.detailTitle`)}>
        <Card style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ marginBottom: 16, color: 'var(--color-text)' }}>{t(`parties.${config.key}.notFound`)}</div>
          <Button variant="secondary" icon="chevron-right" onClick={() => navigate(`/app/${config.key}`)} style={{ transform: 'none' }}>{t('parties.actions.back')}</Button>
        </Card>
      </Page>
    )
  }

  const action = (
    <div style={{ display: 'flex', gap: 10 }}>
      <Button variant="secondary" icon={config.key === 'customers' ? 'customers' : 'supplier'} onClick={() => navigate(`/app/${config.key}`)}>{t('parties.actions.back')}</Button>
      <Button variant="primary" icon="pencil" onClick={() => setEditing(true)}>{t('parties.actions.edit')}</Button>
    </div>
  )

  const vatBroken = party.vat_number && !isValidVat(party.vat_number)
  const vatFix = vatBroken ? suggestBelgianVat(party.vat_number) : null

  return (
    <Page title={party.name} action={action} maxWidth={1000}>
      {vatBroken && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 18, background: 'var(--color-danger-bg)', border: '1px solid var(--color-danger)', borderRadius: 12, flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--color-danger)', flexShrink: 0 }}><Icon name="alert-triangle" size={18} /></span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--color-danger)' }}>{t('parties.vatBroken.title', { vat: party.vat_number })}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text)' }}>
              {t('parties.vatBroken.message')}
              {vatFix && <> {t('parties.vatBroken.suggestion', { vat: vatFix })}</>}
            </div>
          </div>
          <Button variant="danger" icon="pencil" onClick={() => setEditing(true)}>{t('parties.vatBroken.fix')}</Button>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 18, alignItems: 'start' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Info */}
          <Card>
            <SectionTitle>{t(`parties.${config.key}.detailTitle`)}</SectionTitle>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
              <Info label={party.non_vat_liable ? t('parties.fields.companyNumber') : t('parties.fields.vatNumber')} value={party.vat_number} />
              {party.email && <Info label={t('parties.fields.email')} value={<a href={`mailto:${party.email}`} style={{ color: 'var(--color-primary)' }}>{party.email}</a>} />}
              {party.phone && <Info label={t('parties.fields.phone')} value={party.phone} />}
              {[party.contact_first_name, party.contact_last_name].filter(Boolean).length > 0 && <Info label={t('parties.fields.contact')} value={[party.contact_first_name, party.contact_last_name].filter(Boolean).join(' ')} />}
              {config.hasPaymentTerms && party.payment_terms_days != null && <Info label={t('parties.fields.paymentTermsDays')} value={`${party.payment_terms_days} j`} />}
              {config.hasLanguage && party.preferred_language && <Info label={t('parties.fields.language')} value={party.preferred_language.toUpperCase()} />}
            </div>
            {(party.address || party.city) && (
              <div style={{ marginTop: 12 }}>
                <Info label={t('parties.fields.address')} value={<>{party.address}{party.address_line2 ? <>, {party.address_line2}</> : ''}<br />{party.postal_code} {party.city} {party.country ? `· ${party.country}` : ''}</>} />
              </div>
            )}
          </Card>

          {/* Delivery addresses (customers) */}
          {config.hasDeliveryAddresses && (
            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <SectionTitle style={{ margin: 0 }}>{t('parties.deliveryAddresses.title')}</SectionTitle>
                <Button variant="secondary" icon="plus" onClick={() => setAddingAddr(true)}>{t('parties.deliveryAddresses.add')}</Button>
              </div>
              {addresses.length === 0 && !addingAddr && <div style={{ color: 'var(--color-text-muted)', fontSize: 13.5 }}>{t('parties.deliveryAddresses.empty')}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {addresses.map((a) => (
                  <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '11px 14px', border: '1px solid var(--color-border)', borderRadius: 12 }}>
                    <div style={{ fontSize: 13.5, color: 'var(--color-ink)' }}>
                      {a.name && <div style={{ fontWeight: 600 }}>{a.name}</div>}
                      {a.delivery_party_name && <div style={{ color: 'var(--color-text)' }}>{a.delivery_party_name}</div>}
                      <div style={{ color: 'var(--color-text)' }}>{a.address}, {a.postal_code} {a.city} {a.country}</div>
                    </div>
                    <IconButton icon="trash" variant="danger" title={t('common.delete')} onClick={() => deleteAddress(a)} />
                  </div>
                ))}
              </div>
              {addingAddr && <AddressForm entity={config.endpoint} entityId={id} onCancel={() => setAddingAddr(false)} onSaved={() => { setAddingAddr(false); toast(t('parties.toast.saved'), 'success'); load() }} t={t} />}
            </Card>
          )}

          {/* Bank accounts (suppliers, read-only) */}
          {config.hasBankAccounts && (party.bank_accounts?.length > 0) && (
            <Card>
              <SectionTitle>{t('parties.bankAccounts')}</SectionTitle>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {party.bank_accounts.map((a) => (
                  <div key={a.id} style={{ padding: '11px 14px', border: '1px solid var(--color-border)', borderRadius: 12 }}>
                    <div style={{ fontWeight: 600, color: 'var(--color-ink)' }}>{a.account_holder || party.name}{a.is_default && <span style={{ marginLeft: 8 }}><Badge tone="info">{t('parties.default')}</Badge></span>}</div>
                    <div style={{ fontFamily: 'monospace', fontSize: 13.5 }}>{a.iban}</div>
                    {a.bic && <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{a.bic}{a.bank_name ? ` · ${a.bank_name}` : ''}</div>}
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Peppol panel */}
        <Card>
          <SectionTitle>Peppol</SectionTitle>
          <PeppolPanel entity={config.endpoint} party={party} notFoundTone={config.peppolNotFoundTone} onVerified={(p) => setParty((prev) => ({ ...prev, ...p }))} />
        </Card>
      </div>

      {/* Related invoices */}
      <Card padding={0} style={{ marginTop: 18, overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-border-row)' }}>
          <SectionTitle style={{ margin: 0 }}>{t('parties.invoices.title')}</SectionTitle>
        </div>
        {invoices.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)' }}>{t('parties.invoices.empty')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {invoices.map((inv) => {
                const st = invoiceStatus(inv); const pp = peppolBadge(inv)
                return (
                  <tr key={inv.id} style={{ borderTop: '1px solid var(--color-border-row)', cursor: 'pointer' }} onClick={() => navigate(`/app/invoices/${inv.id}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-lilac)')} onMouseLeave={(e) => (e.currentTarget.style.background = '')}>
                    <td style={{ padding: '12px 20px', fontWeight: 600, color: 'var(--color-ink)' }}>{inv.invoice_number || `#${inv.id}`}</td>
                    <td style={{ padding: '12px 20px', color: 'var(--color-text)' }}>{formatDate(inv.issue_date)}</td>
                    <td style={{ padding: '12px 20px', textAlign: 'right', fontFamily: 'var(--font-title)', fontWeight: 600 }}>{formatCurrency(inv.amount_including_tax)}</td>
                    <td style={{ padding: '12px 20px' }}>{pp && <Badge tone={pp.tone}>{pp.key ? t(pp.key) : pp.raw}</Badge>}</td>
                    <td style={{ padding: '12px 20px' }}><Badge tone={st.tone}>{t(st.key)}</Badge></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </Card>

      <PartyFormModal open={editing} config={config} entityId={id} initial={party} onClose={() => setEditing(false)} onSaved={() => { toast(t('parties.toast.saved'), 'success'); load() }} />
    </Page>
  )
}

function AddressForm({ entity, entityId, onCancel, onSaved, t }) {
  const [f, setF] = useState({ name: '', delivery_party_name: '', address: '', postal_code: '', city: '', country: 'BE' })
  const [saving, setSaving] = useState(false)
  const valid = f.address && f.city && f.postal_code && f.country
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const save = async () => {
    setSaving(true)
    try { await api.post(`${entity}/${entityId}/delivery-addresses`, f); onSaved() }
    finally { setSaving(false) }
  }
  return (
    <div style={{ marginTop: 12, padding: 14, border: '1px solid var(--color-border)', borderRadius: 12, background: 'var(--color-bg-lilac)' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label={t('parties.deliveryAddresses.name')}><Input value={f.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label={t('parties.deliveryAddresses.deliveryPartyName')}><Input value={f.delivery_party_name} onChange={(e) => set('delivery_party_name', e.target.value)} /></Field>
      </div>
      <Field label={t('parties.fields.address')} required><Input value={f.address} onChange={(e) => set('address', e.target.value)} /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 12 }}>
        <Field label={t('parties.fields.postalCode')} required><Input value={f.postal_code} onChange={(e) => set('postal_code', e.target.value)} /></Field>
        <Field label={t('parties.fields.city')} required><Input value={f.city} onChange={(e) => set('city', e.target.value)} /></Field>
        <Field label={t('parties.fields.country')} required><Input maxLength={2} value={f.country} onChange={(e) => set('country', e.target.value.toUpperCase())} /></Field>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <Button variant="secondary" onClick={onCancel}>{t('common.cancel')}</Button>
        <Button variant="primary" icon="check" disabled={!valid} loading={saving} onClick={save}>{t('common.save')}</Button>
      </div>
    </div>
  )
}

function SectionTitle({ children, style }) {
  return <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 14, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 12, ...style }}>{children}</h3>
}
function Info({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 14, color: 'var(--color-ink)' }}>{value || '—'}</div>
    </div>
  )
}
